/**
 * TraderPanel — a single trader window that can be embedded in MultiTraderWorkspace.
 *
 * Wraps EquityTrader, FXTrader, BondTrader, or ETFTrader in a panel container
 * with a title bar that shows the trader type and control buttons (detach, close).
 *
 * Architecture:
 *   - Receives type, id, and shared BrokerClient/WsClient from parent
 *   - Renders the appropriate trader component based on type
 *   - Provides detach (pop-out) and close buttons in panel header
 *   - Detach opens trader in a new window (via windowManager)
 *   - Emits onClose event when user clicks close or trader exits
 *   - Minimal styling — layout and sizing handled by grid parent
 *   - Shows visual indicator when trader is detached to separate window
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { BrokerClient } from '../api/brokerClient';
import type { WsClient } from '../api/wsClient';
import { FXTrader } from './FXTrader';
import { BondTrader } from './BondTrader';
import { ETFTrader } from './ETFTrader';
import { windowManager } from '../services/windowManager';

// Note: EquityTrader doesn't exist yet; using Terminal as equity trading interface
// When EquityTrader is built, import it here instead
import { Terminal } from './Terminal';

export type TraderType = 'equity' | 'fx' | 'bonds' | 'etf';

export interface TraderPanelProps {
  id: string;
  type: TraderType;
  brokerClient: BrokerClient;
  wsClient: WsClient;
  onClose?: () => void;
  onDetach?: (id: string, type: TraderType) => void;
  isDetached?: boolean;
  isMobileVisible?: boolean;
  onTitleClick?: () => void;
  onToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
  onRegisterNavHandler?: (id: string, handlers: { handleNav: (k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => void }) => void;
  onFocus?: () => void;
  isActive?: boolean;
}

/**
 * Get display label for trader type
 */
function getTraderTypeLabel(type: TraderType): string {
  return {
    equity: 'Equity Trader',
    fx: 'FX Trader',
    bonds: 'Bond Trader',
    etf: 'ETF Trader',
  }[type];
}

export function TraderPanel({
  id,
  type,
  brokerClient,
  wsClient,
  onClose,
  onDetach,
  isDetached = false,
  isMobileVisible = true,
  onTitleClick,
  onToast,
  onRegisterNavHandler,
  onFocus,
  isActive = false,
}: TraderPanelProps) {
  const label = getTraderTypeLabel(type);
  const [isDetachLoading, setIsDetachLoading] = useState(false);
  const [isCurrentlyDetached, setIsCurrentlyDetached] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<{ handleNav: (k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => void } | null>(null);

  /**
   * Map TraderType to windowManager TraderType
   */
  const mapTraderType = (traderType: TraderType): 'stock' | 'forex' | 'bond' | 'etf' => {
    const typeMap: Record<TraderType, 'stock' | 'forex' | 'bond' | 'etf'> = {
      equity: 'stock',
      fx: 'forex',
      bonds: 'bond',
      etf: 'etf',
    };
    return typeMap[traderType];
  };

  /**
   * Register trader's navigation handler with parent
   */
  useEffect(() => {
    if (onRegisterNavHandler && terminalRef.current) {
      onRegisterNavHandler(id, {
        handleNav: (k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => {
          terminalRef.current?.handleNav(k);
        },
      });
    }
  }, [id, onRegisterNavHandler]);

  /**
   * Check if trader is currently detached (synced with windowManager)
   */
  useEffect(() => {
    const checkDetachedStatus = () => {
      const isDetached = windowManager.isTraderDetached(id);
      setIsCurrentlyDetached(isDetached);
    };

    checkDetachedStatus();

    // Listen for window events
    const onWindowOpen = () => checkDetachedStatus();
    const onWindowClose = () => checkDetachedStatus();

    windowManager.on('onWindowOpen', onWindowOpen);
    windowManager.on('onWindowClose', onWindowClose);

    return () => {
      windowManager.off('onWindowOpen', onWindowOpen);
      windowManager.off('onWindowClose', onWindowClose);
    };
  }, [id]);

  /**
   * Set up scroll detection logging for panel content
   */
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    // Log initial dimensions
    const logDimensions = () => {
      console.log(`[${type.toUpperCase()} Trader #${id.slice(-8)}] Panel content dimensions:`, {
        scrollHeight: contentElement.scrollHeight,
        scrollWidth: contentElement.scrollWidth,
        clientHeight: contentElement.clientHeight,
        clientWidth: contentElement.clientWidth,
        scrollable: {
          vertical: contentElement.scrollHeight > contentElement.clientHeight,
          horizontal: contentElement.scrollWidth > contentElement.clientWidth,
        },
      });
    };

    // Log initial state
    logDimensions();

    // Log on scroll events
    const handleScroll = () => {
      console.log(`[${type.toUpperCase()} Trader #${id.slice(-8)}] Panel scroll event:`, {
        scrollTop: contentElement.scrollTop,
        scrollLeft: contentElement.scrollLeft,
        scrollHeight: contentElement.scrollHeight,
        scrollWidth: contentElement.scrollWidth,
        clientHeight: contentElement.clientHeight,
        clientWidth: contentElement.clientWidth,
      });
    };

    contentElement.addEventListener('scroll', handleScroll, { passive: true });

    // Log when window resizes (panel might resize too)
    const handleResize = () => {
      setTimeout(logDimensions, 100);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [id, type]);

  /**
   * Handle detach button click — open trader in a new window
   */
  const handleDetach = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (isDetachLoading || isCurrentlyDetached) return;

      setIsDetachLoading(true);
      try {
        const mappedType = mapTraderType(type);
        const windowRef = windowManager.openTraderWindow(id, mappedType);

        if (windowRef) {
          setIsCurrentlyDetached(true);
          onToast?.(`${label} opened in new window`, 'success');

          // Auth token is already in localStorage (shared across windows)
          // Child window will read it on load via Gated component
          // No additional action needed here

          // Call parent's detach handler if provided
          if (onDetach) {
            onDetach(id, type);
          }
        } else {
          onToast?.(`Failed to open ${label} in new window`, 'error');
          console.error(`Failed to open window for trader ${id}`);
        }
      } catch (err) {
        console.error('Failed to detach trader:', err);
        onToast?.(`Error: Failed to pop out ${label}`, 'error');
      } finally {
        setIsDetachLoading(false);
      }
    },
    [id, type, label, isDetachLoading, isCurrentlyDetached, onDetach, onToast]
  );

  return (
    <div
      ref={panelRef}
      className={`trader-panel ${isDetached ? 'trader-panel-detached' : ''} ${isActive ? 'trader-panel-active' : ''}`}
      data-trader-id={id}
      data-trader-type={type}
      style={{
        display: isMobileVisible ? 'flex' : 'none',
        border: isActive ? '2px solid #00ff00' : '1px solid #333',
        boxShadow: isActive ? '0 0 8px rgba(0, 255, 0, 0.3)' : 'none',
        cursor: 'pointer',
        transition: 'border 0.2s, box-shadow 0.2s',
      }}
      onClick={onFocus}
    >
      {/* Panel header with title and control buttons */}
      <div className="trader-panel-header">
        <div className="trader-panel-header-left">
          <h3
            className="trader-panel-title"
            onClick={onTitleClick}
            style={{ cursor: onTitleClick ? 'pointer' : 'default' }}
          >
            {label}
          </h3>
          <span className="trader-panel-demo-badge" title="This is a demo environment. No real money at risk.">
            (DEMO)
          </span>
          {isCurrentlyDetached && (
            <span className="trader-panel-detached-badge" title="Currently in separate window">
              Detached
            </span>
          )}
        </div>

        <div className="trader-panel-actions">
          {/* Detach/Pop-out button — show if not already detached */}
          {!isCurrentlyDetached && (
            <button
              type="button"
              className="trader-panel-detach-btn"
              onClick={handleDetach}
              disabled={isDetachLoading}
              title={`Pop out ${label} to a new window`}
              aria-label={`Pop out ${label} to a new window`}
            >
              {isDetachLoading ? (
                <>
                  <span className="btn-icon">⟳</span>
                  <span className="btn-label">Opening...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon">⊞</span>
                  <span className="btn-label">Pop Out</span>
                </>
              )}
            </button>
          )}

          {/* Close button */}
          <button
            type="button"
            className="trader-panel-close-btn"
            onClick={onClose}
            title={`Close ${label}`}
            aria-label={`Close ${label}`}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Panel content: render the appropriate trader component */}
      <div ref={contentRef} className="trader-panel-content">
        {type === 'equity' && (
          <Terminal
            ref={terminalRef}
            brokerOverride={brokerClient}
            wsOverride={wsClient}
            initialView="Trade"
          />
        )}

        {type === 'fx' && (
          <FXTrader
            brokerOverride={brokerClient}
            wsOverride={wsClient}
            onExitToEquities={onClose}
          />
        )}

        {type === 'bonds' && (
          <BondTrader
            brokerOverride={brokerClient}
            wsOverride={wsClient}
            onExitToEquities={onClose}
          />
        )}

        {type === 'etf' && (
          <ETFTrader
            brokerOverride={brokerClient}
            wsOverride={wsClient}
            onExitToEquities={onClose}
          />
        )}
      </div>
    </div>
  );
}

export default TraderPanel;
